import React, {useEffect} from 'react';
import { styled } from '@mui/material/styles';
import {campaignService} from "../../services";
import {CircularProgress, Container, Dialog, DialogContent, DialogContentText, DialogTitle, DialogActions, Button} from "@mui/material";
import { DataGrid } from '@mui/x-data-grid';
import IconButton from "@mui/material/IconButton";
import ResponsiveDialogManager from "../dialog/ResponsiveDialogManager";
import {useSnackbar} from "notistack";
import {useTranslation} from "react-i18next";
import PreviewPlayer from "../previewPlayer/PreviewPlayer";
import PlayCircleFilledIcon from '@mui/icons-material/PlayCircleFilled';
import DeleteIcon from '@mui/icons-material/Delete';

const PREFIX = 'CampaignParticipants';

const classes = {
  marginBottom: `${PREFIX}-marginBottom`,
  filter: `${PREFIX}-filter`,
};

const StyledContainer = styled(Container)((
  {
    theme
  }
) => ({

  [`& .${classes.marginBottom}`]: {
    marginBottom: '0.5rem',
  },

  [`& .${classes.filter}`]: {
    display: 'flex',
    justifyContent: 'space-around',
  },

}));

const CampaignParticipants = (props) => {
  const {enqueueSnackbar} = useSnackbar();
  const {t} = useTranslation();
  const [loading, setLoading] = React.useState(true);
  const [loadingDelete, setLoadingDelete] = React.useState(false);
  const [participants, setParticipants] = React.useState([]);
  const [deleteUser, setDeleteUser] = React.useState(null);
  const [isPlayer, setIsPlayer] = React.useState(null);

  const columns = [
    {
      field: '', headerName: '', renderCell: (params) => (
        <div style={{textAlign: 'center'}}>
          <IconButton
            variant="contained"
            color="primary"
            size="small"
            onClick={() => setDeleteUser(params.row)}
          >
            <DeleteIcon />
          </IconButton>
        </div>
      ), width: 50
    },
    {field: 'firstName', headerName: t('campaignsManagement.participants.grid.firstname'), width: 130},
    {field: 'lastName', headerName: t('campaignsManagement.participants.grid.lastname'), width: 130},
    {field: 'email', headerName: t('campaignsManagement.participants.grid.email'), width: 200},
    {field: 'country', headerName: t('campaignsManagement.participants.grid.country'), width: 120},
    {field: 'city', headerName: t('campaignsManagement.participants.grid.city'), width: 120},
    {field: 'role', headerName: t('campaignsManagement.participants.grid.role')},
    {
      field: 'action', headerName: t('campaignsManagement.participants.grid.video'), renderCell: (params) => (
        <div style={{textAlign: 'center'}}>
          <IconButton
            variant="contained"
            color="primary"
            size="small"
            onClick={() => {
              const tmpContent = params.row.content === 'video' ? params.row.video : params.row.content === 'image' ? params.row.video : null;
              const tmpMessage = params.row?.message;
              setIsPlayer({content: tmpContent, message: tmpMessage, type: params.row.content})
            }}
          >
            <PlayCircleFilledIcon />
          </IconButton>
        </div>
      ),
    },
    {field: 'validated', headerName: t('campaignsManagement.participants.grid.validated'), width: 120},
    {field: 'newsletter', headerName: t('campaignsManagement.participants.grid.newsletter'), width: 120},
    {field: 'dataSpread', headerName: t('campaignsManagement.participants.grid.dataSpread'), width: 120},
    {field: 'createdDate', headerName: t('campaignsManagement.participants.grid.creationDate'), width: 150, valueGetter: (params) => params.value.split('T')[0]},
    {field: 'isRead', headerName: t('campaignsManagement.participants.grid.isRead')},
  ];

  useEffect(() => {
    loadData();
  }, [])


  const loadData = () => {
    setLoading(true);
    campaignService.fetchCampaignParticipants(props.target.id).then((result) => {
      setParticipants(result)
    }).finally(() => setLoading(false));
  };

  const handleDeleteUser = () => {
    setLoadingDelete(true);
    campaignService.deleteParticipant(
      props.target.id,
      deleteUser.id,
      () => enqueueSnackbar(t('toasts.deleteCampaignParticipantError'), {variant: 'error'}),
      () => enqueueSnackbar(t('toasts.deleteCampaignParticipantSuccess'), {variant: 'success'}),
      )
    .then(() => {
      setLoadingDelete(false);
      loadData();
      props.reloadData();
      setDeleteUser(null);
    })
  }

  return (
    <StyledContainer>
      <h1>{t('campaignsManagement.participants.title')}</h1>
      <DataGrid
        rows={participants}
        initialState={{
          pagination: { paginationModel: { pageSize: 10 } },
        }}
        pageSizeOptions={[5, 10, 25, 50]}
        columns={columns}
        autoHeight
        disableRowSelectionOnClick
        loading={loading}
      />
      <ResponsiveDialogManager
        onClose={() => setIsPlayer(null)}
        open={isPlayer ? true : false}
        fullScreen
        renderer={
          <PreviewPlayer
            media={isPlayer}
          />
        }
        title={t('campaignsManagement.campaignParticipants')}
      />
      <Dialog
        open={deleteUser ? true : false}
        onClose={() => setDeleteUser(null)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{`Delete the participant ${deleteUser && deleteUser.firstName} ${deleteUser && deleteUser.lastName} ?`}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {t('campaignsManagement.participants.deleteParticipant.content')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
        {loadingDelete ? 
          <CircularProgress size={28} />
        :
          <React.Fragment>
            <Button onClick={() => setDeleteUser(null)} color="primary">
              {t('campaignsManagement.participants.deleteParticipant.decline')}
            </Button>
            <Button onClick={() => handleDeleteUser()} color="primary" autoFocus>
              {t('campaignsManagement.participants.deleteParticipant.confirm')}
            </Button>
          </React.Fragment>
        }
        </DialogActions>
      </Dialog>
    </StyledContainer>
  );
}

export default CampaignParticipants;